export interface TopicContextJSON {
  /**
   * Serialized as an array because that is the shape already on the wire and in the database; a
   * proposal comment thread has exactly one audience.
   */
  audience: Audience[];
  /**
   * The only comment scope a proposal has left. Retained on the wire so the context a browser writes
   * stays byte-identical to the one the server's `ProposalCommentForumTopic` produces — forum lookup
   * matches contexts as strings, so a divergence here would silently create a second thread.
   */
  field: typeof TopicContext.GeneralField;
}

export enum Audience {
  VendorReviewers = "vendorReviewers",
  ClientReviewers = "clientReviewers",
  AllReviewers = "allReviewers",
}

/**
 * The Topic context of a proposal comment forum: the audience the thread is visible to.
 *
 * Contexts used to name a field (and, for redlines, a collection item) as well, which is what gave a
 * negotiation a forum per field per audience. Threads keyed by anything but audience are no longer
 * written; {@link TopicContext.audienceFromContext} still reads them, because their comments are
 * history and belong in the one stream.
 */
export class TopicContext {
  public static readonly GeneralField = "general";

  public static fromJSON(json: TopicContextJSON): TopicContext {
    return new TopicContext(TopicContext.audienceFromValue(json.audience) ?? Audience.ClientReviewers);
  }

  /**
   * The audience a serialized topic context names.
   *
   * Tolerant on purpose: a context may carry its audience as an array (what this class writes and what
   * the browser has always written) or as a bare string (what the Work module's own proposal forum
   * writes), and a thread that predates the consolidation still carries a field alongside it. Anything
   * unreadable is treated as the actor's own party, never as counterparty-visible — an unparseable
   * context must not be the thing that widens a comment's audience.
   */
  public static audienceFromContext(context: string | null | undefined): Audience | undefined {
    if (!context) return undefined;
    try {
      const parsed: unknown = JSON.parse(context);
      if (!parsed || typeof parsed !== "object") return undefined;
      return TopicContext.audienceFromValue((parsed as { audience?: unknown }).audience);
    } catch {
      console.warn("Unreadable proposal comment forum context");
      return undefined;
    }
  }

  /** Whether an audience makes the thread readable by the counterparty. */
  public static audienceIsExternal(audience: Audience | undefined): boolean {
    return audience === Audience.AllReviewers;
  }

  public readonly audience: Audience;

  constructor(audience: Audience) {
    this.audience = audience;
  }

  public isEqualTo(other: TopicContext): boolean {
    return this.audience === other.audience;
  }

  public toJSON(): TopicContextJSON {
    return { field: TopicContext.GeneralField, audience: [this.audience] };
  }

  private static audienceFromValue(value: unknown): Audience | undefined {
    const candidate = Array.isArray(value) ? value[0] : value;
    if (typeof candidate !== "string") return undefined;
    return Object.values(Audience).find((audience) => audience === candidate);
  }
}
